import axios from 'axios';
import { FILE_UPLOAD_BASE_URL } from './FileStorage';

/**
 * แนบ token ที่ถูกต้องให้ทุก request ที่ยิงไป service file-upload (file-manage)
 *
 * file-manage เป็นคนละแอปกับ isb-api และ verify JWT ด้วย secret ของตัวเอง
 * token ที่ใช้ login เว็บ (x-access-token) จึงผ่าน guard ของมันไม่ได้ ขึ้น
 * 401 "Invalid or expired token" ทุกครั้ง (เป็น error รวมของ jsonwebtoken ไม่ได้แปลว่าหมดอายุ)
 *
 * isb-api จึงมี endpoint auth/fileToken ไว้แลก token อีกใบที่เซ็นด้วย secret ของ file-manage
 * ไฟล์นี้ทำหน้าที่ดัก request ผ่าน axios interceptor ตัวเดียว แล้วสลับ Authorization ให้อัตโนมัติ
 * ทำที่เดียวจบ ไม่ต้องแก้ทุกหน้าที่อัปโหลดไฟล์ (มีสิบกว่าที่) และหน้าใหม่ที่เขียนทีหลังก็ได้ไปด้วย
 */

// cache แยกตาม company และผูกกับ x-access-token ปัจจุบัน
// ถ้าเปลี่ยนคน login (token ต้นทางเปลี่ยน) ต้องขอใหม่ ไม่งั้นจะใช้ token ของคนก่อนหน้าค้างอยู่
const tokenCache = new Map();
const pendingRequests = new Map();

// เผื่อเวลาไว้ 60 วิ กันกรณี token หมดอายุระหว่างที่อัปโหลดไฟล์ใหญ่ยังไม่เสร็จ
const EXPIRY_BUFFER_MS = 60 * 1000;

const cacheKeyOf = (company, sourceToken) => `${company || ''}|${sourceToken || ''}`;

/**
 * แกะ company ออกจาก URL ของ file-manage
 *
 * รูปแบบ path ที่ใช้กันอยู่:
 *   {base}/{company}/{relatedType}/{relatedId}
 *   {base}/{company}/file/{fileId}
 *   {base}/upload/{company}/{relatedType}/{relatedId}
 *   {base}/upload-multiple/{fileName}?Company={company}
 *
 * คืน '' ถ้าแกะไม่ออก — ฝั่ง API จะ fallback ไปใช้ company ของผู้ใช้เองอยู่แล้ว
 * (จำเป็นเฉพาะ superuser ที่ไม่มี company ของตัวเอง)
 */
const parseCompanyFromUrl = (url) => {
    try {
        const [pathPart, queryPart] = url.split('?');

        // ตัดส่วนหน้าออกด้วย base ก่อน ถ้าไม่ตรง (โค้ดเก่าที่ hardcode host อื่น) ค่อยหาจาก /file-manage
        let rest = '';
        if (FILE_UPLOAD_BASE_URL && pathPart.startsWith(FILE_UPLOAD_BASE_URL)) {
            rest = pathPart.slice(FILE_UPLOAD_BASE_URL.length);
        } else {
            const match = /\/file-manage[a-z]*\//i.exec(pathPart);
            if (!match) return '';
            rest = pathPart.slice(match.index + match[0].length);
        }

        const segments = rest.split('/').filter(Boolean);

        if (segments.length === 0) return '';

        if (segments[0] === 'upload-multiple') {
            const match = /(?:^|&)Company=([^&]*)/i.exec(queryPart || '');
            return match ? decodeURIComponent(match[1]) : '';
        }

        if (segments[0] === 'upload') {
            return segments[1] || '';
        }

        return segments[0];
    } catch (error) {
        return '';
    }
};

const isFileManageUrl = (url) => {
    if (!url) return false;
    if (FILE_UPLOAD_BASE_URL && url.startsWith(FILE_UPLOAD_BASE_URL)) return true;
    // เผื่อโค้ดเก่าที่ hardcode host อื่นไว้ (file-manageqa / file-managedev / services.ismartsales.com)
    return /\/file-manage[a-z]*\//i.test(url);
};

/**
 * ขอ token สำหรับ file-manage จาก isb-api (แคชไว้จนใกล้หมดอายุ)
 * @returns {Promise<string|null>} token หรือ null ถ้าขอไม่สำเร็จ
 */
export const getFileUploadToken = async (company = '') => {
    const sourceToken = localStorage.getItem('x-access-token');
    if (!sourceToken) return null;

    const key = cacheKeyOf(company, sourceToken);

    const cached = tokenCache.get(key);
    if (cached && cached.expiresAt - EXPIRY_BUFFER_MS > Date.now()) {
        return cached.token;
    }

    // ถ้ามีคำขอค้างอยู่แล้วให้รอใบเดียวกัน กันกรณีอัปโหลดหลายไฟล์พร้อมกันแล้วยิงขอ token ซ้ำ
    const pending = pendingRequests.get(key);
    if (pending) return pending;

    const url = `${process.env.REACT_APP_API_URL}auth/fileToken${company ? `?company=${encodeURIComponent(company)}` : ''}`;

    const request = axios
        .get(url, { headers: { Authorization: `Bearer ${sourceToken}` } })
        .then((result) => {
            const data = result.data?.data;
            if (!data || !data.token) {
                console.error('getFileUploadToken: ไม่ได้รับ token', result.data?.errors);
                return null;
            }

            tokenCache.set(key, {
                token: data.token,
                expiresAt: Date.now() + (data.expiresIn || 900) * 1000,
            });

            return data.token;
        })
        .catch((error) => {
            console.error('getFileUploadToken error:', error);
            return null;
        })
        .finally(() => {
            pendingRequests.delete(key);
        });

    pendingRequests.set(key, request);
    return request;
};

/** ล้าง cache (ใช้ตอน logout) */
export const clearFileUploadToken = () => {
    tokenCache.clear();
    pendingRequests.clear();
};

let installed = false;

/**
 * ติดตั้ง interceptor — เรียกครั้งเดียวตอนแอปเริ่มทำงาน
 */
export const installFileUploadAuthInterceptor = () => {
    if (installed) return;
    installed = true;

    axios.interceptors.request.use(async (config) => {
        const url = config.url || '';
        if (!isFileManageUrl(url)) return config;

        const token = await getFileUploadToken(parseCompanyFromUrl(url));
        if (!token) return config;

        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return config;
    });
};
